export class LocalStorageManager {
    private storageKey: string;

    constructor(localStorageKey: string) {
        this.storageKey = localStorageKey;
    }

    setItem<T>(value: T): void {
        try {
            const jsonString = JSON.stringify(value);
            localStorage.setItem(this.storageKey, jsonString);
        } catch (error) {
            console.error(`Failed to set item in localStorage with key "${this.storageKey}":`, error);
        }
    }

    getItem<T>(defaultValue?: T): T | null {
        try {
            const jsonString = localStorage.getItem(this.storageKey);
            if (jsonString === null) {
                return defaultValue ?? null;
            }
            return JSON.parse(jsonString) as T;
        } catch (error) {
            console.error(`Failed to get item from localStorage with key "${this.storageKey}":`, error);
            return defaultValue ?? null;
        }
    }

    setNestedItem<T>(nestedKey: string, value: T): void {
        try {
            const jsonString = localStorage.getItem(this.storageKey);
            const data = jsonString ? JSON.parse(jsonString) as Record<string, any> : {};
            const keys = nestedKey.split('.');
            const lastKey = keys.pop();
            const lastObj = keys.reduce((acc, part) => acc[part] = acc[part] ?? {}, data);
            if (lastKey) {
                lastObj[lastKey] = value;
            }
            localStorage.setItem(this.storageKey, JSON.stringify(data));
        } catch (error) {
            console.error(`Failed to set nested item with key "${nestedKey}":`, error);
        }
    }

    getNestedItem<T>(nestedKey: string, defaultValue?: T): T | null {
        try {
            const jsonString = localStorage.getItem(this.storageKey);
            if (jsonString === null) {
                return defaultValue ?? null;
            }
            const data = JSON.parse(jsonString) as Record<string, any>;
            return nestedKey.split('.').reduce((acc, part) => acc?.[part], data) ?? defaultValue ?? null;
        } catch (error) {
            console.error(`Failed to get nested item with key "${nestedKey}":`, error);
            return defaultValue ?? null;
        }
    }

    removeNestedItem(nestedKey: string): void {
        try {
            const jsonString = localStorage.getItem(this.storageKey);
            if (jsonString === null) {
                return;
            }

            const data = JSON.parse(jsonString) as Record<string, any>;
            const keys = nestedKey.split('.');
            const lastKey = keys.pop();
            const lastObj = keys.reduce((acc, part) => acc[part] = acc[part] ?? {}, data);

            if (lastKey && lastObj) {
                delete lastObj[lastKey];
                localStorage.setItem(this.storageKey, JSON.stringify(data));
            }
        } catch (error) {
            console.error(`Failed to remove nested item with key "${nestedKey}":`, error);
        }
    }
}
